import React, { useEffect, useState } from 'react';
import {
  Box,
  styled,
  Grid,
  Typography,
  Button,
  InputAdornment,
  TextField,
  Autocomplete,
  Paper
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import PageHeader from '../../../components/PageHeader';
import SummaryNumberInOut from './SummaryNumberInOut';
import TableInOutEachGate from './TableInOutEachGate';

import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { api } from '../../../api';
import { Company } from '../../../schema/company';
import { Gate } from '../../../schema/gate';
import { PersonType } from '../../../schema/person';
import MultiSelect from '../../../components/MultiSelect';

import { DateTimePicker } from '@mui/x-date-pickers';
import { CCTV_TYPE_ID, getCCTVSearchParams } from './filter';

const CCTVreport = () => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const [filterID, setFilterID] = useState<string>('');
  const [filterName, setFilterName] = useState<string>('');
  const [filterGateIDList, setFilterGateIDList] = useState<number[]>([]);
  const [filterCompanyIDList, setFilterCompanyIDList] = useState<number[]>([]);
  const [filterPersonTypeIDList, setFilterPersonTypeIDList] = useState<string[]>([]);
  const [filterStartDate, setFilterStartDate] = useState<Date | null>(today);
  const [filterEndDate, setFilterEndDate] = useState<Date | null>(null);

  const [filterID2, setFilterID2] = useState<string>('');
  const [filterName2, setFilterName2] = useState<string>('');
  const [filterGateIDList2, setFilterGateIDList2] = useState<number[]>([]);
  const [filterCompanyIDList2, setFilterCompanyIDList2] = useState<number[]>([]);
  const [filterPersonTypeIDList2, setFilterPersonTypeIDList2] = useState<string[]>([]);
  const [filterStartDate2, setFilterStartDate2] = useState<Date | null>(today);
  const [filterEndDate2, setFilterEndDate2] = useState<Date | null>(null);

  const [companies, setCompanies] = useState<Company[]>([]);
  const [searchCompanyKeyword, setSearchCompanyKeyword] = useState<string>('');
  const [gates, setGates] = useState<Gate[]>([]);
  const [personTypes, setPersonTypes] = useState<PersonType[]>([]);

  const [isExportLoading, setIsExportLoading] = useState<boolean>(false);

  const handleExportCSVFile = () => {
    if (isExportLoading) {
      return;
    }
    const searchParams = getCCTVSearchParams({
      filterID: filterID2,
      filterName: filterName2,
      filterGateIDList: filterGateIDList2,
      filterCompanyIDList: filterCompanyIDList2,
      filterPersonTypeIDList: filterPersonTypeIDList2,
      filterStartDate: filterStartDate2,
      filterEndDate: filterEndDate2
    });
    searchParams.set('type_id', `${CCTV_TYPE_ID}`);

    setIsExportLoading(true);
    api
      .get(`/scan-in-out/export`, { params: searchParams, responseType: 'blob' })
      .then((response) => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'cctv-scan_export_data.xlsx');
        document.body.appendChild(link);
        link.click();
      })
      .finally(() => setIsExportLoading(false));
  };

  const handleEnterToSearch = () => {
    setFilterID2(filterID);
    setFilterName2(filterName);
    setFilterGateIDList2(filterGateIDList);
    setFilterCompanyIDList2(filterCompanyIDList);
    setFilterPersonTypeIDList2(filterPersonTypeIDList);
    setFilterStartDate2(filterStartDate);
    setFilterEndDate2(filterEndDate);
  };

  useEffect(() => {
    api.get<Company[]>(`/company`).then((res) => {
      const { data } = res;
      setCompanies(data);
    });
    api.get<PersonType[]>(`/person-type`).then((res) => {
      const { data } = res;
      setPersonTypes(data);
    });
    api.get<Gate[]>(`/gate`).then((res) => {
      const { data } = res;
      // CCTV gates only
      setGates(data.filter((gate) => gate.gate_type_id === CCTV_TYPE_ID));
    });
  }, []);

  useEffect(() => {
    if (searchCompanyKeyword) {
      const timeOut = setTimeout(() => {
        const searchParams = new URLSearchParams();
        searchParams.set('company_name', searchCompanyKeyword);
        searchParams.set('limit', '10');

        api.get<Company[]>(`/company`, { params: searchParams }).then((res) => {
          const { data } = res;
          setCompanies(data);
        });
      }, 500);
      return () => clearTimeout(timeOut);
    }
  }, [searchCompanyKeyword]);

  return (
    <Container>
      <PageHeader text="รายงานแสกนใบหน้า" />

      <Box sx={{ marginTop: 2 }}>
        <SummaryNumberInOut
          filterID={filterID2}
          filterName={filterName2}
          filterGateIDList={filterGateIDList2}
          filterCompanyIDList={filterCompanyIDList2}
          filterPersonTypeIDList={filterPersonTypeIDList2}
          filterStartDate={filterStartDate2}
          filterEndDate={filterEndDate2}
        />

        <Paper sx={{ my: 2 }}>
          <Box display="flex" justifyContent="space-between" sx={{ p: 2, pb: 0 }}>
            <Typography variant="h6" display="inline" sx={{ py: 1 }}>
              รายการแสกนใบหน้าด้วยกล้อง CCTV
            </Typography>
            <Box>
              <Button variant="outlined" onClick={handleExportCSVFile} disabled={isExportLoading}>
                Export
              </Button>
              <Button style={{ marginLeft: 10 }} variant="outlined" onClick={handleEnterToSearch}>
                ค้นหา
              </Button>
            </Box>
          </Box>
          <Grid container spacing={2} sx={{ padding: 2 }}>
            <Grid item md={3}>
              <TextField
                id="search-text-label"
                label="ค้นหาเลขบัตรประชาชน"
                variant="outlined"
                placeholder="ค้นหาเลขบัตรประชาชน"
                onChange={(event) => setFilterID(event.target.value)}
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  )
                }}
                autoComplete="off"
              />
            </Grid>
            <Grid item md={3}>
              <TextField
                id="search-text-label"
                label="ค้นหาชื่อ - นามสกุล"
                variant="outlined"
                placeholder="ค้นหาชื่อ - นามสกุล"
                onChange={(event) => setFilterName(event.target.value)}
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  )
                }}
                autoComplete={'off'}
              />
            </Grid>
            <Grid item md={3}>
              <MultiSelect
                options={personTypes.map((data) => ({
                  id: data.id.toString(),
                  name: data.type_name
                }))}
                onChange={(data) => setFilterPersonTypeIDList(data)}
                label="ประเภทบุคคล"
              />
            </Grid>
            <Grid item md={3}>
              <Autocomplete
                multiple
                limitTags={2}
                disablePortal
                options={companies}
                getOptionLabel={(option) => option.company_name}
                renderInput={(params) => <TextField {...params} label="บริษัท" fullWidth />}
                onChange={(_, value) => setFilterCompanyIDList(value.map((data) => data.id))}
                onInputChange={(_, newInputValue) => {
                  setSearchCompanyKeyword(newInputValue);
                }}
                fullWidth
              />
            </Grid>
            <Grid item md={3}>
              <Autocomplete
                multiple
                limitTags={2}
                disablePortal
                options={gates}
                getOptionLabel={(option) => option.gate_name}
                renderInput={(params) => <TextField {...params} label="ประตู (กล้อง CCTV)" fullWidth />}
                onChange={(_, value) => setFilterGateIDList(value.map((data) => data.id))}
                fullWidth
              />
            </Grid>
            <Grid item md={3}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DateTimePicker
                  label="วันที่เริ่มต้น"
                  inputFormat="dd/MM/yyyy HH:mm"
                  value={filterStartDate}
                  onChange={(value) => setFilterStartDate(value)}
                  renderInput={(params) => <TextField {...params} fullWidth />}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item md={3}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DateTimePicker
                  label="วันที่สิ้นสุด"
                  inputFormat="dd/MM/yyyy HH:mm"
                  value={filterEndDate}
                  onChange={(value) => setFilterEndDate(value)}
                  renderInput={(params) => <TextField {...params} fullWidth />}
                />
              </LocalizationProvider>
            </Grid>
          </Grid>
        </Paper>

        <TableInOutEachGate
          filterID={filterID2}
          filterName={filterName2}
          filterGateIDList={filterGateIDList2}
          filterCompanyIDList={filterCompanyIDList2}
          filterPersonTypeIDList={filterPersonTypeIDList2}
          filterStartDate={filterStartDate2}
          filterEndDate={filterEndDate2}
        />
      </Box>
    </Container>
  );
};

export default CCTVreport;

const Container = styled('div')`
  padding: 16px;
`;
